import React, { useCallback } from 'react'
import AuthContextLayer from '@web/walter-lib/auth'
import { AppSvg, FormattedHTMLMessage, icons, useNotification } from '@web/walter-ui-kit'
import { useResetUserSettings } from '@web/walter-lib/user-settings'
import { useConfig } from '@web/walter-lib/config'
import { lang } from './lang'
import { authApi } from '~api'
import { Routes as AppRoutes } from '~constants/routes'

interface Props {
    children: React.ReactNode
}

const getErrorNotification = () => ({
    intent: 'notification' as const,
    createdAt: Date.now(),
    icon: <AppSvg svgName={icons.ic18CircleCrossedOut} />,
    title: <FormattedHTMLMessage {...lang.error} />,
    content: <FormattedHTMLMessage {...lang.content} />,
})

/**
 * AuthContext
 */
function AuthLayer(props: Props) {
    const config = useConfig()
    const { resetUserSettings } = useResetUserSettings()
    const { push } = useNotification()

    const initialAuth = config?.userMeta?.isAuth ?? false

    const onLogout = useCallback(
        async (_: (isAuth: boolean) => void, isAuth: boolean, unauth?: boolean, path?: string) => {
            if (isAuth) {
                try {
                    await authApi.deleteSessions({ current: true, unauth })
                    resetUserSettings(true)

                    if (window.isPosthogInit) {
                        // posthog's reset will crash if it's not initialized, so call it safely
                        // https://github.com/PostHog/posthog-js/issues/323
                        try {
                            window.posthog?.reset()
                        } catch (e) {
                            // do nothing
                        }
                    }

                    window.location.href = path || `/${AppRoutes.signIn}`
                } catch {
                    push(getErrorNotification())
                }
            }
        },
        [push, resetUserSettings]
    )

    const onLogin = useCallback(
        (setAuth: (isAuth: boolean) => void) => {
            resetUserSettings()
            setAuth(true)
        },
        [resetUserSettings]
    )

    return (
        <AuthContextLayer initialAuth={initialAuth} onLogin={onLogin} onLogout={onLogout}>
            {props.children}
        </AuthContextLayer>
    )
}

export default AuthLayer
